import {
  FeatureName,
  useCurrentWorkspace,
  useIsFeatureEnabled,
  useStorageKeys,
  WorkspaceType,
} from '@gripp/shared-logic';
import {
  AlertsButton,
  AssetsList,
  EntityAlertListModal,
  SelectionStatus,
} from '@gripp/shared-ui';
import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { AddonsButton } from './addonsButton';
import { RoutinesButton } from './routinesButton';

export type SelectedAssetRow = {
  id: string;
  groups?: Array<{ id: string; name: string }>;
};

type AssetComponentProps = {
  onRowsSelectedChange?: (rows: SelectedAssetRow[]) => void;
  selectedRowIds?: string[];
  selectionStatus?: SelectionStatus;
  onSelectionStatusChange?: (status: SelectionStatus) => void;
};

export const AssetComponent = ({
  onRowsSelectedChange,
  selectedRowIds,
  selectionStatus: externalSelectionStatus,
  onSelectionStatusChange: externalOnSelectionStatusChange,
}: AssetComponentProps) => {
  const isCropCircleEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);
  const navigate = useNavigate();
  const { assetId } = useParams();
  const workspace = useCurrentWorkspace(true);
  const storageKeys = useStorageKeys();
  const [internalSelectionStatus, setInternalSelectionStatus] =
    useState<SelectionStatus>('idle');

  const selectionStatus = externalSelectionStatus ?? internalSelectionStatus;
  const setSelectionStatus = externalOnSelectionStatusChange ?? setInternalSelectionStatus;

  const [showAlertListModal, setShowAlertListModal] = useState(false);
  const [selectedAssetIdForAlert, setSelectedAssetIdForAlert] = useState<
    string | undefined
  >(undefined);

  const onAssetClick = (asset: { id: string }) => {
    navigate(`/assets/${asset.id}`);
  };

  const updateRowsSelected = (rows: Array<any>) => {
    onRowsSelectedChange?.(
      rows.map((row) => ({
        id: row.id,
        groups: row.groups,
      }))
    );
  };

  const selectedIdsSet = useMemo(
    () => new Set(selectedRowIds ?? []),
    [selectedRowIds]
  );
  const isRowSelected = useCallback(
    (row: { id?: string }) => (row?.id ? selectedIdsSet.has(row.id) : false),
    [selectedIdsSet]
  );

  return (
    <View
      style={[
        styles.assetsContainer,
        isCropCircleEnabled && styles.assetsContainerCropCircle,
      ]}
    >
      <AssetsList
        onAssetClick={onAssetClick}
        updateRowsSelected={updateRowsSelected}
        isRowSelected={isRowSelected}
        preserveScrollKey={storageKeys.assetsScrollData}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={(status) => setSelectionStatus(status)}
        routinesButton={RoutinesButton}
        addonsButton={workspace?.type === WorkspaceType.Oem ? AddonsButton : undefined}
        alertsButton={(data) => (
          <AlertsButton
            alertsCount={data.alertsCount}
            assetId={data.assetId}
            name={data.name}
            onPress={() => {
              setSelectedAssetIdForAlert(data.assetId);
              setShowAlertListModal(true);
            }}
          />
        )}
        selectedAssetId={assetId}
      />
      {showAlertListModal && selectedAssetIdForAlert && (
        <EntityAlertListModal
          visible={showAlertListModal}
          entityType="asset"
          entityId={selectedAssetIdForAlert}
          onClose={() => {
            setShowAlertListModal(false);
            setSelectedAssetIdForAlert(undefined);
          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  assetsContainer: {
    flex: 1,
    borderRadius: 4,
    overflow: 'hidden',
    zIndex: -1,
  },
  assetsContainerCropCircle: {
    borderRadius: 0,
  },
});
